import qs from 'query-string';
import axios from 'axios';
import type { WebAuth, Auth0Callback, Auth0Error } from 'auth0-js';
import { config } from './env.config';
import { executeWithDeferredLibrary } from './utils';
import { getLocalStorage } from '@shared/core';

const CLIENT_ID = config.auth0ClientId;
const REDIRECT_URI = `${config.clientUri}/account-callback`;
const SERVER_URI = `${config.serverUri}/identity`;

const [resolveWebAuth, executeWithWebAuth] = executeWithDeferredLibrary<WebAuth>();

function getAuth0Domain(env: string | undefined) {
  return env === 'development' && getLocalStorage().getItem('enableAuth0CustomDomain') ? 'accounts.withjoy.com' : 'withjoy.auth0.com';
}

export const initAuth0 = () => {
  // auth0-js is a browser library and as such, is not instantiated on the server during SSR.
  // Normal imports at the top of the file would result in a error log.
  // https://github.com/auth0/auth0.js/issues/877#issuecomment-665743985
  import(
    /* webpackChunkName: "static/webpack/vendor/auth0-js" */
    'auth0-js'
  ).then(({ WebAuth }) => {
    resolveWebAuth(
      new WebAuth({
        domain: getAuth0Domain(config.joyEnvironment),
        clientID: CLIENT_ID,
        redirectUri: REDIRECT_URI,
        audience: 'https://withjoy.auth0.com/userinfo',
        responseType: 'code',
        scope: 'openid offline_access'
      })
    );
  });
};

export type Auth0AuthorizeRedirect = {
  uri?: string;
  failUri?: string;
};

export type Auth0AuthorizeResult = {
  code: string;
  redirectJSON: string;
};

const authorize = (connection: string, redirect: Auth0AuthorizeRedirect) => {
  executeWithWebAuth(auth => {
    auth.authorize({ connection, redirectUri: REDIRECT_URI, state: JSON.stringify(redirect) });
  });
};

const deriveAuthorizeResult = (urlSearch: string): Auth0AuthorizeResult => {
  const parsedUrl = qs.parse(urlSearch);
  const code = parsedUrl['code'] as string;
  const redirectJSON = parsedUrl['state'] as string;
  return { code, redirectJSON };
};

const hydrateAuthorizeRedirect = (state: string): Auth0AuthorizeRedirect => {
  // the caller must `try { } catch { }` as needed
  return JSON.parse(state);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const login = (email: string, password: string, cb: Auth0Callback<any, Auth0Error>) => {
  executeWithWebAuth(auth => {
    auth.login({ realm: 'Username-Password-Authentication', email, password }, cb);
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const signup = (email: string, password: string, cb: Auth0Callback<any, Auth0Error>) => {
  executeWithWebAuth(auth => {
    auth.signup({ connection: 'Username-Password-Authentication', email, password }, cb);
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const changePassword = (email: string, cb: Auth0Callback<any, Auth0Error>) => {
  executeWithWebAuth(auth => {
    auth.changePassword({ connection: 'Username-Password-Authentication', email }, cb);
  });
};

const signout = (returnToUrl?: string) => {
  executeWithWebAuth(auth => {
    auth.logout({
      clientID: CLIENT_ID,
      // not sure if we want this yet
      // federated: true,
      // not using returnTo at this time, it's not configured
      // returnTo: returnToUrl || config.clientUri
      ...(returnToUrl ? { returnTo: returnToUrl } : {})
    });
  });
};

const refreshToken = (code: string) => {
  return axios.post(`${SERVER_URI}/refreshToken`, { redirectUri: REDIRECT_URI, code });
};

export const auth0 = {
  authorize,
  deriveAuthorizeResult,
  hydrateAuthorizeRedirect,
  login,
  signup,
  signout,
  changePassword,
  refreshToken
};

export type Auth0 = typeof auth0;
